import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Loader2 } from "lucide-react";
import { submitLead } from "../utils/analytics";
import LegalDocModal from "./LegalDocModal";

/**
 * Приводит ввод к виду +7 (999) 123-45-67.
 * Считаем только цифры: так корректно отрабатывают вставка из буфера,
 * удаление символов и номера, начинающиеся с 8 или +7.
 */
function formatPhone(raw) {
  let digits = raw.replace(/\D/g, "");

  // Пустое поле должно оставаться пустым, иначе «+7» не стереть
  if (!digits) return "";

  // 8 и 7 в начале — это один и тот же код страны
  if (digits.startsWith("8")) digits = "7" + digits.slice(1);
  if (!digits.startsWith("7")) digits = "7" + digits;
  digits = digits.slice(0, 11);

  const rest = digits.slice(1);
  let out = "+7";
  if (rest.length) out += " (" + rest.slice(0, 3);
  if (rest.length >= 3) out += ") " + rest.slice(3, 6);
  if (rest.length >= 6) out += "-" + rest.slice(6, 8);
  if (rest.length >= 8) out += "-" + rest.slice(8, 10);
  return out;
}

export default function LeadForm({
  source = "lead_form",
  title = "Запишитесь на консультацию",
  subtitle = "Перезвоним в течение 15 минут и подберём удобное время визита",
  buttonText = "Записаться на консультацию",
  extraPayload = {},
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [agree, setAgree] = useState(true);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [legal, setLegal] = useState(null); // 'privacy' | 'consent' | null

  const validate = () => {
    const e = {};
    if (!name.trim() || name.trim().length < 2) e.name = "Введите ваше имя";
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length !== 11) e.phone = "Введите номер полностью";
    if (!agree) e.agree = "Необходимо согласие на обработку персональных данных";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await submitLead({ name: name.trim(), phone: phone.trim(), source, ...extraPayload });
      navigate("/thank-you");
    } catch (err) {
      console.warn("submit error", err);
      navigate("/thank-you");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <form className="lead-form" onSubmit={handleSubmit} data-testid={`lead-form-${source}`}>
        <h3>{title}</h3>
        <p className="lead-form-sub">{subtitle}</p>

        <div className={`form-field ${errors.name ? "error" : ""}`}>
          <label htmlFor={`name-${source}`}>Ваше имя</label>
          <input
            id={`name-${source}`}
            type="text"
            placeholder="Как к вам обращаться"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid={`input-name-${source}`}
            autoComplete="name"
          />
          {errors.name && <div className="form-error">{errors.name}</div>}
        </div>

        <div className={`form-field ${errors.phone ? "error" : ""}`}>
          <label htmlFor={`phone-${source}`}>Телефон</label>
          <input
            id={`phone-${source}`}
            type="tel"
            placeholder="+7 (___) ___-__-__"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            onFocus={() => { if (!phone) setPhone("+7 ("); }}
            inputMode="tel"
            data-testid={`input-phone-${source}`}
            autoComplete="tel"
          />
          {errors.phone && <div className="form-error">{errors.phone}</div>}
        </div>

        <label className={`form-consent-check ${errors.agree ? "error" : ""}`} data-testid={`consent-check-${source}`}>
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            data-testid={`consent-input-${source}`}
          />
          <span>
            Я ознакомлен(а) и даю{" "}
            <button
              type="button"
              className="link-btn"
              onClick={() => setLegal("consent")}
              data-testid={`open-consent-${source}`}
            >
              согласие на обработку персональных данных
            </button>{" "}
            и соглашаюсь с{" "}
            <button
              type="button"
              className="link-btn"
              onClick={() => setLegal("privacy")}
              data-testid={`open-privacy-${source}`}
            >
              политикой обработки персональных данных
            </button>
          </span>
        </label>
        {errors.agree && <div className="form-error" style={{ marginTop: -8, marginBottom: 12 }}>{errors.agree}</div>}

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={sending}
          data-testid={`submit-${source}`}
        >
          {sending ? (
            <>
              <Loader2 size={18} className="spinner" />
              Отправляем…
            </>
          ) : (
            <>
              <Phone size={18} />
              {buttonText}
            </>
          )}
        </button>
      </form>

      <LegalDocModal open={!!legal} docKey={legal} onClose={() => setLegal(null)} />
    </>
  );
}
